// Month calendar + time column for picking an availability slot.
//
// Replaces the old flat "every day stacked vertically" list. Days with
// availability are the only clickable ones; picking a day reveals its times
// beside the calendar (below it on mobile).
//
// All date maths is done on ET calendar parts (year/month/day integers), never
// by converting a Date to the browser's local zone. Slots are published in ET
// and a visitor in Los Angeles must see the same day boundaries as one in
// Atlanta, so a slot at 9:00 PM ET belongs to that ET day even though it is
// still the previous day in California.

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Text, VStack, HStack, Grid, Badge, Icon } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ET_TZ = 'America/New_York';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "2026-09-28" for the ET calendar day a given instant falls on.
const etDayKey = (() => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return (value) => {
    const parts = {};
    for (const { type, value: v } of fmt.formatToParts(new Date(value))) parts[type] = v;
    return `${parts.year}-${parts.month}-${parts.day}`;
  };
})();

const dayKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// Date.UTC keeps these off the local clock: we only want calendar arithmetic.
const daysInMonth = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const firstWeekday = (y, m) => new Date(Date.UTC(y, m, 1)).getUTCDay();

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: ET_TZ,
  });

const fmtLongDay = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
};

const SlotCalendar = ({ slots, selectedSlot, onSelect }) => {
  // Slots grouped by ET day, each group time-ordered.
  const byDay = useMemo(() => {
    const groups = {};
    for (const slot of slots) {
      const key = etDayKey(slot.start_time);
      (groups[key] = groups[key] || []).push(slot);
    }
    for (const list of Object.values(groups)) {
      list.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    }
    return groups;
  }, [slots]);

  const availableDays = useMemo(() => Object.keys(byDay).sort(), [byDay]);
  const firstAvailable = availableDays[0];
  const todayKey = useMemo(() => etDayKey(new Date()), []);

  const [view, setView] = useState(() => {
    const base = firstAvailable || todayKey;
    const [y, m] = base.split('-').map(Number);
    return { y, m: m - 1 };
  });
  const [selectedDay, setSelectedDay] = useState(firstAvailable || null);

  // When the slot set changes (different meeting type), jump to the first day
  // that actually has availability rather than leaving the user on an empty month.
  useEffect(() => {
    if (!firstAvailable) {
      setSelectedDay(null);
      return;
    }
    const [y, m] = firstAvailable.split('-').map(Number);
    setView({ y, m: m - 1 });
    setSelectedDay(firstAvailable);
  }, [firstAvailable]);

  const monthHasEarlier = availableDays.some(
    (k) => k < dayKey(view.y, view.m, 1),
  );
  const monthHasLater = availableDays.some(
    (k) => k > dayKey(view.y, view.m, daysInMonth(view.y, view.m)),
  );

  const step = (delta) => {
    setView(({ y, m }) => {
      const next = m + delta;
      if (next < 0) return { y: y - 1, m: 11 };
      if (next > 11) return { y: y + 1, m: 0 };
      return { y, m: next };
    });
  };

  const cells = [];
  const lead = firstWeekday(view.y, view.m);
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth(view.y, view.m); d += 1) cells.push(d);

  const daySlots = selectedDay ? byDay[selectedDay] || [] : [];

  return (
    <Grid
      templateColumns={{ base: '1fr', md: 'minmax(0, 1fr) minmax(0, 260px)' }}
      gap={{ base: 6, md: 8 }}
      bg="gray.900"
      border="1px solid"
      borderColor="gray.700"
      borderRadius="md"
      p={{ base: 4, md: 6 }}
    >
      {/* ── Month grid ── */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Button
            aria-label="Previous month"
            onClick={() => step(-1)}
            isDisabled={!monthHasEarlier}
            variant="ghost"
            size="sm"
            minW="32px"
            px={2}
            color="gray.300"
            _hover={{ bg: 'gray.800', color: 'green.300' }}
          >
            <Icon as={ChevronLeft} boxSize={4} />
          </Button>
          <Text fontWeight="bold" color="white" fontSize={{ base: 'sm', md: 'md' }} letterSpacing="wide">
            {MONTHS[view.m]} {view.y}
          </Text>
          <Button
            aria-label="Next month"
            onClick={() => step(1)}
            isDisabled={!monthHasLater}
            variant="ghost"
            size="sm"
            minW="32px"
            px={2}
            color="gray.300"
            _hover={{ bg: 'gray.800', color: 'green.300' }}
          >
            <Icon as={ChevronRight} boxSize={4} />
          </Button>
        </HStack>

        <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
          {WEEKDAYS.map((w) => (
            <Text key={w} textAlign="center" fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
              {w}
            </Text>
          ))}
        </Grid>

        <Grid templateColumns="repeat(7, 1fr)" gap={1}>
          {cells.map((d, i) => {
            if (d === null) return <Box key={`pad-${i}`} />;
            const key = dayKey(view.y, view.m, d);
            const has = Boolean(byDay[key]);
            const isSelected = key === selectedDay;
            const isToday = key === todayKey;
            return (
              <Box
                key={key}
                as="button"
                type="button"
                disabled={!has}
                aria-label={`${fmtLongDay(key)}${has ? `, ${byDay[key].length} times available` : ', no availability'}`}
                aria-current={isSelected ? 'date' : undefined}
                onClick={() => has && setSelectedDay(key)}
                position="relative"
                h={{ base: '38px', md: '42px' }}
                borderRadius="md"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight={has ? '600' : '400'}
                bg={isSelected ? 'green.500' : has ? 'gray.800' : 'transparent'}
                color={isSelected ? 'white' : has ? 'green.200' : 'gray.600'}
                cursor={has ? 'pointer' : 'default'}
                border="1px solid"
                borderColor={isToday && !isSelected ? 'green.700' : 'transparent'}
                transition="all 0.15s"
                _hover={has && !isSelected ? { bg: 'gray.700', borderColor: 'green.600' } : {}}
              >
                {d}
                {has && !isSelected && (
                  <Box
                    position="absolute"
                    bottom="5px"
                    left="50%"
                    transform="translateX(-50%)"
                    w="4px"
                    h="4px"
                    borderRadius="full"
                    bg="green.400"
                  />
                )}
              </Box>
            );
          })}
        </Grid>

        <HStack mt={4} spacing={4} fontSize="xs" color="gray.500">
          <HStack spacing={2}>
            <Box w="4px" h="4px" borderRadius="full" bg="green.400" />
            <Text>Available</Text>
          </HStack>
          <Text>All times Eastern (ET)</Text>
        </HStack>
      </Box>

      {/* ── Time column ── */}
      <Box borderLeft={{ base: 'none', md: '1px solid' }} borderTop={{ base: '1px solid', md: 'none' }} borderColor="gray.700" pl={{ base: 0, md: 6 }} pt={{ base: 5, md: 0 }}>
        {selectedDay ? (
          <>
            <Text color="green.400" fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" mb={4}>
              {fmtLongDay(selectedDay)}
            </Text>
            <VStack spacing={2} align="stretch" maxH={{ md: '300px' }} overflowY={{ md: 'auto' }} pr={{ md: 1 }}>
              {daySlots.map((slot) => {
                const active = selectedSlot?.id === slot.id;
                return (
                  <Button
                    key={slot.id}
                    onClick={() => onSelect(slot)}
                    variant="outline"
                    size="md"
                    justifyContent="space-between"
                    fontWeight="600"
                    bg={active ? 'green.500' : 'transparent'}
                    color={active ? 'white' : 'gray.200'}
                    borderColor={active ? 'green.500' : 'gray.600'}
                    _hover={{ borderColor: 'green.500', bg: active ? 'green.500' : 'gray.800' }}
                  >
                    {fmtTime(slot.start_time)}
                    {slot.mode && (
                      <Badge
                        ml={2}
                        bg={active ? 'green.700' : 'gray.700'}
                        color={active ? 'white' : 'green.300'}
                        fontSize="10px"
                        textTransform="uppercase"
                      >
                        {slot.mode === 'virtual' ? 'Virtual' : 'In person'}
                      </Badge>
                    )}
                  </Button>
                );
              })}
            </VStack>
          </>
        ) : (
          <Text color="gray.500" fontSize="sm">
            Select a highlighted day to see its times.
          </Text>
        )}
      </Box>
    </Grid>
  );
};

export default SlotCalendar;
